import { MAX_LOS, MIN_LOS } from './packageCategories';
import type { RoomAssetLink } from './types';

export type ParsedRoomRow = {
  name: string;
  pricesByLos: Record<number, number>;
};

export type ParsedOffer = {
  roomRows: Array<ParsedRoomRow>;
  losColumns: Array<{ columnIndex: number; los: number }>;
};

const MIN_PRICE = 100;
const MAX_PRICE = 200000;

function toNumber(cell: string): number {
  return parseFloat((cell || '').replace(/[$,\s]/g, ''));
}

function splitRow(line: string): Array<string> {
  return line.includes('\t') ? line.split('\t') : line.split(/\s{2,}/);
}

export function parsePastedOffer(text: string): ParsedOffer {
  const rows = text
    .split('\n')
    .map((line) => line.replace(/\r$/, ''))
    .filter((line) => line.trim())
    .map(splitRow);

  let losRow: Array<string> | undefined;
  for (const cells of rows) {
    const numbers = cells
      .slice(1)
      .map(toNumber)
      .filter((value) => !isNaN(value));
    if (numbers.length >= 1 && numbers.every((value) => value >= MIN_LOS && value <= MAX_LOS)) {
      losRow = cells;
    }
  }

  const losColumns: ParsedOffer['losColumns'] = [];
  losRow?.forEach((cell, columnIndex) => {
    const los = toNumber(cell);
    if (columnIndex > 0 && !isNaN(los) && los >= MIN_LOS && los <= MAX_LOS) {
      losColumns.push({ columnIndex, los: Math.trunc(los) });
    }
  });

  const roomRows: Array<ParsedRoomRow> = [];
  for (const cells of rows) {
    const name = (cells[0] || '').trim();
    if (!name || cells === losRow) {
      continue;
    }
    const pricesByLos: Record<number, number> = {};
    for (const { columnIndex, los } of losColumns) {
      const value = toNumber(cells[columnIndex]);
      if (!isNaN(value) && value >= MIN_PRICE && value < MAX_PRICE) {
        pricesByLos[los] = value;
      }
    }
    if (Object.keys(pricesByLos).length > 0) {
      roomRows.push({ name, pricesByLos });
    }
  }

  return { roomRows, losColumns };
}

function normalise(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export type RoomAssetMatch = { asset: RoomAssetLink } | { ambiguous: true } | undefined;

// Asset names repeat on an Account, so a shared name resolves only when exactly one match is already linked.
export function matchRoomAsset(
  pastedName: string,
  assets: Array<RoomAssetLink>,
  linkedAssetIds: Array<string> = [],
): RoomAssetMatch {
  const left = normalise(pastedName);
  const candidates = left ? assets.filter((asset) => asset.name && normalise(asset.name) === left) : [];
  if (candidates.length <= 1) {
    return candidates[0] ? { asset: candidates[0] } : undefined;
  }
  const linked = candidates.filter((asset) => linkedAssetIds.includes(asset.id));
  return linked.length === 1 ? { asset: linked[0] } : { ambiguous: true };
}
